import pb from './pocketbase';

// Configuration
const REFRESH_BEFORE_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes avant expiration
const MIN_REFRESH_INTERVAL_MS = 60 * 1000; // Minimum 1 minute entre les checks

let refreshTimeoutId: ReturnType<typeof setTimeout> | null = null;
let isRefreshing = false;

/**
 * Decode JWT payload sans validation (côté client, pour lire exp)
 */
function decodeJwtPayload(token: string): { exp?: number } | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    
    // Le payload est la 2ème partie, encodée en base64url
    const payload = parts[1];
    // Convertir base64url en base64
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    // Décoder
    const jsonStr = atob(base64);
    return JSON.parse(jsonStr);
  } catch (e) {
    console.warn('[auth-refresh] Failed to decode JWT:', e);
    return null;
  }
}

/**
 * Calcule le temps restant avant expiration du token (en ms)
 */
function getTimeUntilExpiry(token: string): number | null {
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) return null;
  
  // exp est en secondes Unix
  const expiryMs = payload.exp * 1000;
  const now = Date.now();
  return expiryMs - now;
}

/**
 * Refresh le token via l'API Pocketbase
 */
async function refreshToken(): Promise<boolean> {
  if (isRefreshing) {
    console.log('[auth-refresh] Refresh already in progress, skipping');
    return false;
  }
  
  isRefreshing = true;
  
  try {
    console.log('[auth-refresh] Refreshing token...');
    
    // Utiliser l'endpoint de refresh Pocketbase
    const response = await fetch('https://api.simpleclair.fr/api/collections/_superusers/auth-refresh', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${pb.authStore.token}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      throw new Error(`Refresh failed: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.token && data.record) {
      // Sauvegarder le nouveau token
      pb.authStore.save(data.token, data.record);
      console.log('[auth-refresh] Token refreshed successfully');
      return true;
    } else {
      throw new Error('Invalid refresh response');
    }
  } catch (error) {
    console.error('[auth-refresh] Refresh failed:', error);
    return false;
  } finally {
    isRefreshing = false;
  }
}

/**
 * Planifie le prochain refresh du token
 */
function scheduleNextRefresh(): void {
  // Annuler tout timeout existant
  if (refreshTimeoutId) {
    clearTimeout(refreshTimeoutId);
    refreshTimeoutId = null;
  }
  
  const token = pb.authStore.token;
  if (!token) {
    console.log('[auth-refresh] No token, not scheduling refresh');
    return;
  }
  
  const timeUntilExpiry = getTimeUntilExpiry(token);
  if (timeUntilExpiry === null) {
    console.warn('[auth-refresh] Could not determine token expiry');
    return;
  }
  
  // Calculer quand refresh (5 min avant expiration)
  let refreshInMs = timeUntilExpiry - REFRESH_BEFORE_EXPIRY_MS;
  
  // S'assurer qu'on ne refresh pas trop souvent
  if (refreshInMs < MIN_REFRESH_INTERVAL_MS) {
    refreshInMs = MIN_REFRESH_INTERVAL_MS;
  }
  
  // Si le token expire dans moins de 5 minutes, refresh immédiatement
  if (timeUntilExpiry <= REFRESH_BEFORE_EXPIRY_MS) {
    console.log('[auth-refresh] Token expiring soon, refreshing immediately');
    handleRefresh();
    return;
  }
  
  const refreshInMinutes = Math.round(refreshInMs / 60000);
  console.log(`[auth-refresh] Next refresh scheduled in ${refreshInMinutes} minutes`);
  
  refreshTimeoutId = setTimeout(handleRefresh, refreshInMs);
}

/**
 * Gère le refresh et replanifie si succès, sinon redirect login
 */
async function handleRefresh(): Promise<void> {
  const success = await refreshToken();
  
  if (success) {
    // Replanifier le prochain refresh
    scheduleNextRefresh();
  } else {
    // Refresh échoué → déconnecter et rediriger vers login
    console.log('[auth-refresh] Refresh failed, redirecting to login');
    pb.authStore.clear();
    
    // Dispatch un événement custom pour que l'app puisse réagir
    window.dispatchEvent(new CustomEvent('auth:expired'));
    
    // Fallback: redirection directe
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }
}

/**
 * Démarre le monitoring du token pour auto-refresh
 */
export function startTokenRefresh(): void {
  console.log('[auth-refresh] Starting token refresh monitoring');
  
  // Vérifier si le token actuel est valide
  if (!pb.authStore.isValid || !pb.authStore.token) {
    console.log('[auth-refresh] No valid token, not starting monitoring');
    return;
  }
  
  const timeUntilExpiry = getTimeUntilExpiry(pb.authStore.token);
  if (timeUntilExpiry !== null) {
    const minutesLeft = Math.round(timeUntilExpiry / 60000);
    console.log(`[auth-refresh] Token expires in ${minutesLeft} minutes`);
  }
  
  // Planifier le premier refresh
  scheduleNextRefresh();
}

/**
 * Arrête le monitoring du token
 */
export function stopTokenRefresh(): void {
  console.log('[auth-refresh] Stopping token refresh monitoring');
  
  if (refreshTimeoutId) {
    clearTimeout(refreshTimeoutId);
    refreshTimeoutId = null;
  }
}

/**
 * Force un refresh immédiat (utile après reconnexion)
 */
export async function forceRefresh(): Promise<boolean> {
  stopTokenRefresh();
  const success = await refreshToken();
  if (success) {
    scheduleNextRefresh();
  }
  return success;
}

// Écouter les changements d'auth pour ajuster le monitoring
pb.authStore.onChange(() => {
  if (pb.authStore.isValid) {
    scheduleNextRefresh();
  } else {
    stopTokenRefresh();
  }
});
